import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Phone, Mail, MapPin, Send, MessageCircle } from 'lucide-react';
import { companyInfo } from '../mockData';
import { useToast } from '../hooks/use-toast';

const ContactSection = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: 'Gros Œuvre',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi du message');
      }

      toast({
        title: "Message envoyé !",
        description: "Nous vous contacterons dans les plus brefs délais.",
        duration: 5000,
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        projectType: 'Gros Œuvre',
        message: ''
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'envoi. Veuillez réessayer.",
        variant: "destructive",
        duration: 5000,
      });
      console.error('Erreur:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const projectTypes = ['Gros Œuvre', 'Second Œuvre', 'Promotion Immobilière', 'Autre'];

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-100 rounded-full blur-3xl opacity-30"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-20 space-y-4">
          <span className="bg-gradient-to-r from-yellow-500 to-yellow-400 text-white px-5 py-2.5 rounded-full text-sm font-semibold shadow-lg inline-flex items-center gap-2">
            <MessageCircle size={16} />
            Contact
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
            Parlons de
            <span className="block mt-2 bg-gradient-to-r from-yellow-500 to-yellow-400 bg-clip-text text-transparent">
              votre projet
            </span>
          </h2>
          <p className="text-xl text-gray-600">
            Notre équipe est à votre écoute pour étudier vos besoins
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {/* Contact info cards */}
            <Card className="group border-2 border-gray-100 hover:border-yellow-200 bg-white hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-14 h-14 bg-gradient-to-br from-yellow-500 to-yellow-400 rounded-2xl flex items-center justify-center mx-auto shadow-lg group-hover:scale-110 transition-transform">
                  <Phone className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Téléphone 1</h3>
                  <a href={`tel:${companyInfo.phone}`} className="text-sm text-gray-600 hover:text-yellow-600 transition-colors font-medium">
                    {companyInfo.phone}
                  </a>
                  <p className="text-xs text-gray-500 mt-2">Lun-Ven : 8h-18h</p>
                </div>
              </CardContent>
            </Card>

            <Card className="group border-2 border-gray-100 hover:border-yellow-200 bg-white hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-14 h-14 bg-gradient-to-br from-yellow-500 to-yellow-400 rounded-2xl flex items-center justify-center mx-auto shadow-lg group-hover:scale-110 transition-transform">
                  <Phone className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Téléphone 2</h3>
                  <a href={`tel:${companyInfo.phone2}`} className="text-sm text-gray-600 hover:text-yellow-600 transition-colors font-medium">
                    {companyInfo.phone2}
                  </a>
                  <p className="text-xs text-gray-500 mt-2">Lun-Ven : 8h-18h</p>
                </div>
              </CardContent>
            </Card>

            <Card className="group border-2 border-gray-100 hover:border-yellow-200 bg-white hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-14 h-14 bg-gradient-to-br from-yellow-500 to-yellow-400 rounded-2xl flex items-center justify-center mx-auto shadow-lg group-hover:scale-110 transition-transform">
                  <Mail className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Email</h3>
                  <a href={`mailto:${companyInfo.email}`} className="text-gray-600 hover:text-yellow-600 transition-colors break-all text-sm font-medium">
                    {companyInfo.email}
                  </a>
                  <p className="text-xs text-gray-500 mt-2">Réponse sous 24h</p>
                </div>
              </CardContent>
            </Card>

            <Card className="group border-2 border-gray-100 hover:border-yellow-200 bg-white hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-14 h-14 bg-gradient-to-br from-yellow-500 to-yellow-400 rounded-2xl flex items-center justify-center mx-auto shadow-lg group-hover:scale-110 transition-transform">
                  <MapPin className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Adresse</h3>
                  <p className="text-sm text-gray-600 font-medium">{companyInfo.address}</p>
                  <p className="text-xs text-gray-500 mt-2">Sur rendez-vous</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact form */}
          <Card className="border-2 border-yellow-100 bg-white shadow-2xl">
            <CardContent className="p-10">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-gray-900 mb-2">Demander un devis gratuit</h3>
                <p className="text-gray-600">Remplissez le formulaire et nous vous recontacterons rapidement</p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-gray-900 font-semibold">Nom complet *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Votre nom"
                      className="border-gray-200 h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-900 font-semibold">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="votre@email.com"
                      className="border-gray-200 h-12"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-gray-900 font-semibold">Téléphone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+33 6 12 34 56 78"
                      className="border-gray-200 h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="projectType" className="text-gray-900 font-semibold">Type de projet *</Label>
                    <select
                      id="projectType"
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleChange}
                      required
                      className="flex h-12 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
                    >
                      {projectTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-gray-900 font-semibold">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Décrivez votre projet..."
                    rows={6}
                    className="border-gray-200 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  size="lg"
                  className="w-full bg-gradient-to-r from-yellow-500 to-yellow-400 hover:from-blue-700 hover:to-blue-600 text-white py-7 text-lg group shadow-xl hover:shadow-2xl transition-all"
                >
                  {isSubmitting ? 'Envoi en cours...' : (
                    <>
                      Envoyer ma demande
                      <Send className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
